import { VCS_CALL_POLICY_SESSION_KEY } from 'consts';
import { callPolicyDraftFromMessenger } from 'features/CallSettings/utils/joinPolicyFromMessenger';
import type { CallPolicyDraft } from 'state/store';
import { callPolicyStore } from 'state/store';
import { selectCallPolicyDraft } from 'state/selectors';

type CallPolicySessionData = {
    bootstrap?: CallPolicyDraft;
    prejoinDraft?: CallPolicyDraft;
    /** true после того как bootstrap был успешно отправлен на сервер. Сбрасывается при новом bootstrap от messenger. */
    bootstrapSynced?: boolean;
};

const policiesEqual = (a: CallPolicyDraft, b: CallPolicyDraft): boolean =>
    a.joinPolicy === b.joinPolicy && a.moderatorsApprovalOnly === b.moderatorsApprovalOnly;

const readSession = (): CallPolicySessionData => {
    if (typeof sessionStorage === 'undefined') {
        return {};
    }

    const raw = sessionStorage.getItem(VCS_CALL_POLICY_SESSION_KEY);
    if (!raw) {
        return {};
    }

    try {
        return JSON.parse(raw) as CallPolicySessionData;
    } catch {
        return {};
    }
};

const writeSession = (data: CallPolicySessionData): void => {
    const hasBootstrap = data.bootstrap != null;
    const hasPrejoinDraft = data.prejoinDraft != null;

    if (!hasBootstrap && !hasPrejoinDraft) {
        sessionStorage.removeItem(VCS_CALL_POLICY_SESSION_KEY);
        return;
    }

    sessionStorage.setItem(VCS_CALL_POLICY_SESSION_KEY, JSON.stringify(data));
};

export const clearCallPolicySession = (): void => {
    sessionStorage.removeItem(VCS_CALL_POLICY_SESSION_KEY);
};

export const applyCallPolicyBootstrap = (joinApprovalPolicy: string | null): void => {
    if (joinApprovalPolicy == null) return;

    const bootstrap = callPolicyDraftFromMessenger({
        joinApprovalPolicy,
    });

    writeSession({ ...readSession(), bootstrap, prejoinDraft: undefined, bootstrapSynced: undefined });
};

/** Snapshot политики из messenger bootstrap (session), без учёта правок на prejoin. */
export const getCallPolicyBootstrapFromSession = (): CallPolicyDraft | null => readSession().bootstrap ?? null;

export const hasPendingPrejoinCallPolicySync = (): boolean => {
    const session = readSession();
    const bootstrap = session.bootstrap;

    if (!bootstrap) {
        // standalone web: нет messenger bootstrap тогда pending только если юзер явно сохранил draft
        return session.prejoinDraft != null;
    }

    // прользователь поменял политику на prejoin
    if (!policiesEqual(selectCallPolicyDraft(), bootstrap)) {
        return true;
    }

    // bootstrap еще не синкнут на сервер (startCallV2 не обновляет LiveKit metadata).
    // синкаем при первом входе инициатора, чтобы metadata комнаты отразила нужную политику.
    return !session.bootstrapSynced;
};

/** После успешного sync на сервер - обновить bootstrap snapshot под текущий draft. */
export const persistCallPolicyBootstrapFromDraft = (): void => {
    const draft = selectCallPolicyDraft();
    writeSession({ ...readSession(), bootstrap: { ...draft }, prejoinDraft: undefined, bootstrapSynced: true });
};

/** Сохранить правки prejoin draft в session (переживает F5 до входа в комнату). */
export const persistPrejoinCallPolicyDraft = (): void => {
    const draft = selectCallPolicyDraft();
    writeSession({ ...readSession(), prejoinDraft: { ...draft } });
};

export const clearPrejoinCallPolicyDraft = (): void => {
    const session = readSession();
    if (!session.prejoinDraft) {
        return;
    }
    writeSession({ ...session, prejoinDraft: undefined });
};

export const getRestoredCallPolicyDraftFromSession = (): CallPolicyDraft | null => {
    const session = readSession();
    return session.prejoinDraft ?? session.bootstrap ?? null;
};

/** Восстановить draft политики на prejoin после F5 (сначала pending draft, иначе bootstrap). */
export const restoreCallPolicyDraftFromSession = (): boolean => {
    const restored = getRestoredCallPolicyDraftFromSession();
    if (!restored) {
        return false;
    }
    callPolicyStore.set(restored);
    return true;
};
